import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'rebass/styled-components';

import {
  CompanyNameRenderer,
  ContactNameRenderer,
  CompanyRowRenderer,
  ContactOfRenderer,
} from 'components/molecules/renderers';
import { getRowClass, processCellForClipboard } from 'helpers/cell';
import DataTable from '../../../../components/molecules/agGrid';
import { useCompanyTable } from '../../hooks/useCompanyTable';
import { ContactsSelector } from '../contactsSelector';
import { ShareYourContacts } from '../shareYourContacts';
import { IntroOfferRenderer } from './components/introOfferRenderer';
import { IntroOfferTooltip } from './components/introOfferTooltip';

export const Table = ({ sharedList, user }) => {
  const {
    companiesColumns,
    isLoading,
    isFetching,
    queryParams,
    tableRows,
    totalItems,
    hasMore,
    initialSortBy,
    networkOptions,
    selectedNetwork,
    contactsCount,
    onLoadMore,
    onChangeContactsSelector,
    isActiveSync,
    ref,
    persistColumnKey,
  } = useCompanyTable({ sharedList, user });

  return (
    <Box
      sx={{
        '& .ag-center-cols-container': {
          '& .ag-row': {
            '& .ag-cell:last-child:not(.ag-cell-range-selected)': {
              borderRight: 'none',
            },

            '&.no-contacts .ag-cell': {
              borderRight: isActiveSync ? 'none' : '1px solid var(--ag-border-color)',
            },
          },
        },
      }}
    >
      <DataTable
        ref={ref}
        itemName="company"
        fixedColumn="last"
        isInitialized={!isLoading}
        canSort={false}
        initialSortBy={initialSortBy}
        hasMore={hasMore}
        loadMore={onLoadMore}
        items={tableRows}
        totalItems={totalItems}
        rowActions={[]}
        enableLeftTableBorder
        cellStyle={{
          padding: '0px',
        }}
        gridOptions={{
          processCellForClipboard,
          getRowClass,
          tooltipShowDelay: 0,
          suppressRowClickSelection: true,
          cellSelection: false,
        }}
        defaultColDef={{
          tooltipComponent: IntroOfferTooltip,
        }}
        components={{
          companyNameRenderer: CompanyNameRenderer,
          contactNameRenderer: ContactNameRenderer,
          companyRowRenderer: CompanyRowRenderer,
          contactOfRenderer: ContactOfRenderer,
          introOfferRenderer: IntroOfferRenderer,
        }}
        columnDefinition={companiesColumns}
        isLoadingMore={isFetching && queryParams.page !== 1}
        persistColumnKey={persistColumnKey}
        tableSx={{
          minWidth: '1360px',
          height: 'fit-content!important',
        }}
        sx={{
          height: 'calc(100vh - 124px)!important',
        }}
        tableActionsSx={{
          pl: '0',
        }}
        rightActions={
          <Box display={['none', 'none', 'block']}>
            <ShareYourContacts sharedList={sharedList} user={user} />
          </Box>
        }
        leftActions={
          networkOptions?.length > 1 ? (
            <Box mx={['16px', '16px', '0']}>
              <ContactsSelector options={networkOptions} value={selectedNetwork} onChange={onChangeContactsSelector} />
            </Box>
          ) : null
        }
        topBanner={
          contactsCount === 0 &&
          !isLoading &&
          !isFetching &&
          user?.linkedinSync?.syncStatus === 'active' &&
          !user?.linkedinSync?.contactsImportInProgress ? (
            <Box>
              <Box
                py="32px"
                my="24px"
                sx={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%238797ABFF' stroke-width='1' stroke-dasharray='10%2c 10' stroke-dashoffset='8' stroke-linecap='square'/%3e%3c/svg%3e")`,
                  borderRadius: 'default',
                }}
              >
                <Box maxWidth="560px" mx="auto" px="16px">
                  <Text fontWeight="medium">No relevant contacts found</Text>
                  <Text fontSize="14px" mt="8px">
                    {`We couldn’t find any contacts matching the desired criteria at these companies. Here’s how you can still help ${sharedList?.pointOfContact?.fullName}:`}
                  </Text>
                  <Box as="ol" p="0" m="0" mt="24px" pl="20px">
                    <Text as="li" mb="8px" fontSize="14px">
                      {`Share your contacts with ${sharedList?.pointOfContact?.fullName} using the button above.`}
                    </Text>
                    <Text as="li" mb="8px" fontSize="14px">
                      Review the companies below and offer intros to people not listed.
                    </Text>
                    <Text as="li" mb="8px" fontSize="14px">
                      Offer intros to relevant people at similar companies.
                    </Text>
                    <Text as="li" mb="8px" fontSize="14px">
                      Share this page with others who might be able to help.
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null
        }
      />
    </Box>
  );
};

Table.propTypes = {
  sharedList: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
